import { standing } from './dancers'
import { ABSENCE_REASONS } from './portal'
import type {
  AttendanceStanding,
  ClassAttendance,
  DancerStanding,
  MissedSession as PortalMissedSession,
  OverallState,
} from './portal'

export type AttendanceState =
  | 'Unmarked'
  | 'Present'
  | 'Late'
  | 'Excused'
  | 'Unexcused'
  /** In the room and not dancing (D-634). Counts as an absence and earns a makeup. */
  | 'Watched'
  /**
   * Never called to this session, so there was nothing to attend (D-842).
   *
   * Not an absence and not a gap: a rehearsal calls part of its roster, and this
   * is the row that says she was not on the call. Only a bulk declaration over a
   * named sub-team writes it - never a teacher on one dancer.
   */
  | 'NotExpected'
export type AttendanceRole = 'Enrolled' | 'MakeupVisitor'

export type AbsenceReason = 'Illness' | 'FamilyConflict' | 'SchoolEvent' | 'Other'

export type SheetDancer = {
  dancerId: string
  name: string
  state: AttendanceState
  role: AttendanceRole
  makesUpFor: string | null
  /** The studio-only note (D-656). Never shown to a family. */
  note: string | null
  /** What the teacher wrote for the family about this session (D-656). */
  familyNote?: string | null
  /**
   * A parent said in advance she would miss this (D-188).
   *
   * Never "excused" — telling the studio is courtesy, not an exemption, and
   * whether Tonya later forgave the session is deliberately absent from this
   * payload entirely.
   */
  reportedOut: boolean
  reason: AbsenceReason | null
  reportedBy: string | null
  reportedAt: string | null
  reportedNote: string | null
  /** A note is on file. The fact only — the file is behind ViewMedicalDocuments (D-9). */
  hasDocument: boolean
}

export type AttendanceSheet = {
  occurrenceId: string
  studioClassId: string
  className: string
  date: string
  startTime: string | null
  endTime: string | null
  roomName: string | null
  instructors: string[]
  enrolled: SheetDancer[]
  makeupVisitors: SheetDancer[]
  marked: number
  isCancelled: boolean
  /** A rehearsal class, where a sub-team can be declared not called (D-842). */
  takesACall: boolean
  /** The sub-team standing as not called for this session, by name (D-851). */
  notCalledTeam: string | null
  /**
   * Whether *this* reader may declare one — the server's answer, never inferred
   * from a capability here (D-855).
   *
   * Reading this sheet says nothing about it — the two questions have different
   * answers for a **substitute** and for a **senior teacher**, which is where
   * `TeachingScope.MayDeclareCallAsync` explains why.
   */
  canDeclareCall: boolean
}

/** What declaring a sub-team not called here would do, before anybody does (D-855). */
export type SessionCallOffer = {
  /** Why this session can take no call at all; then `teams` is empty. */
  refusal: string | null
  teams: SessionCallTeamOffer[]
}

export type SessionCallTeamOffer = {
  teamId: string
  teamName: string
  /**
   * How many of this session's dancers are on the team — the number the button
   * says out loud, so it and the result cannot disagree (D-855).
   */
  covers: number
  /** Already declared, so the control offers the undo. */
  standing: boolean
  /**
   * How many households would be told, which is **not** `covers` (D-847).
   *
   * Sisters share a household, so 27 dancers is about 24 families, and the button
   * names both: the register in front of her shows the 27, and the 24 is what she
   * is about to send. **Zero for a past session**, because a backfill tells nobody
   * (D-845) — so the confirm says so before she taps.
   */
  families: number
}

export type SessionOption = {
  occurrenceId: string
  className: string
  startTime: string | null
  endTime: string | null
  roomName: string | null
  instructors: string[]
  enrolled: number
  marked: number
  musicUrl: string | null
  /**
   * The studio's own name for the routine, where it has one (D-916, D-581).
   *
   * **Carried so `describeClass` can be given both halves.** Handed only a
   * `className` that function parses the Jackrabbit string; handed a routine
   * name too it returns that untouched, which is what the calendar, Home, the
   * portal and this screen's own music browser already print. Until the day
   * list renders it, this field is plumbing — nothing reads it yet.
   *
   * **Optional, so a fixture written before it still type-checks** — the same
   * call `PendingPicture.contentType` made. The server always sends it, with
   * null where a class has no routine name.
   */
  routineName?: string | null
}

export type TeacherOption = { staffId: string; name: string }

/** A class whose register the current account may open (D-189). */
export type RegisterClass = {
  /** How many of this class's sessions are already behind us (D-782). */
  met: number
  classId: string
  name: string
  dayOfWeek: string
  startTime: string | null
  sessions: number
}

export type RegisterSession = {
  occurrenceId: string
  className: string
  date: string
  startTime: string | null
  roomName: string | null
  expected: number
  marked: number
  isIncomplete: boolean
  /** Has not happened yet. Marks are stored but stay out of the 80% until the date passes. */
  isFuture: boolean
  isCancelled: boolean
}

/**
 * One dancer's banked classes (D-745, read by a family since D-757).
 *
 * The studio's list and the family's panel are the same shape because they are
 * the same answer: `GetBankedAsync` counts a bank in one place, and two
 * readers of one function cannot disagree about a number that decides whether
 * a parent goes looking for a makeup she does not need.
 */
export type BankedDancer = {
  dancerId: string
  name: string
  available: number
  applied: number
  kinds: { label: string; available: number; applied: number }[]
}

export async function fetchBanked(): Promise<BankedDancer[]> {
  const res = await fetch('/api/attendance/banked')

  // Named for what it fetches (D-209): a screen joining two reads has to be
  // able to say which one failed.
  if (!res.ok) throw new Error(`Could not load the banked classes (${res.status})`)
  return res.json()
}

/**
 * What one dancer is holding, for her own family (D-757).
 *
 * The other door onto the same count. `asked`: *"it does show the parents what
 * is banked"* — it did not: the family could see a class it was possible to
 * bank and a night a credit had covered, and nowhere the number she carries.
 *
 * Always a row, never an empty list: the dancer is named by the request, so
 * holding nothing is `available: 0` rather than nothing to draw.
 */
export async function fetchBankedFor(dancerId: string): Promise<BankedDancer> {
  const res = await fetch(`/api/portal/dancer/${dancerId}/banked`)
  if (!res.ok) throw new Error(`Could not load her banked classes (${res.status})`)
  return res.json()
}

export async function fetchRegisterClasses(): Promise<RegisterClass[]> {
  const res = await fetch('/api/attendance/missing/classes')
  if (!res.ok) throw new Error(`Could not load the classes (${res.status})`)
  return res.json()
}

export async function fetchRegisterSessions(classId: string): Promise<RegisterSession[]> {
  const res = await fetch(`/api/attendance/missing/class/${classId}`)
  if (!res.ok) throw new Error(`Could not load that class's dates (${res.status})`)
  return res.json()
}

/**
 * The session a class picker should land on.
 *
 * The most recent one that has already happened, else the first upcoming one.
 * "Default to right now" is what somebody means when they open a class — and
 * before the season starts there is no past session, so the first week of term
 * is the only sensible answer.
 */
export function defaultSession(sessions: RegisterSession[]): RegisterSession | null {
  if (sessions.length === 0) return null

  const past = sessions.filter((s) => !s.isFuture)
  if (past.length > 0) return past[past.length - 1]

  return sessions[0]
}

export type DeviceContext = {
  deviceName: string
  roomName: string | null
  /** The day on screen, which is not always today (D-235). */
  date: string
  /** The studio's own today, in America/Chicago (D-68). */
  today: string
  now: SessionOption[]
  elsewhere: SessionOption[]
  teachers: TeacherOption[]
}

/** A dancer and where she should be, and deliberately nothing else (D-233). */
export type KioskDancerHit = {
  dancerId: string
  name: string
  today: KioskPlacement[]
}

export type KioskPlacement = {
  className: string
  roomName: string | null
  startTime: string | null
  endTime: string | null
}

export type MusicFolder = {
  classId: string
  className: string
  routineName: string | null
  musicUrl: string
}

/**
 * Where this tablet's registration token lives.
 *
 * `localStorage`, not a cookie: the token identifies the *device* and must
 * survive a browser restart and outlive any teacher's session. It is set once,
 * when the tablet is first put in the room.
 */
const TOKEN_KEY = 'cda.device.token'

export function deviceToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    // Private browsing, or storage disabled by policy. The screen says it is
    // not registered rather than throwing, which is at least actionable.
    return null
  }
}

export function setDeviceToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token.trim())
  } catch {
    /* Nothing useful to do — the caller re-reads and reports it as unregistered. */
  }
}

export function deviceHeaders(): HeadersInit {
  const token = deviceToken()
  return token ? { 'X-CDA-Device': token } : {}
}

/**
 * The day's sessions for this device (D-235).
 *
 * The date is optional and the server falls back to the studio's own today —
 * never the browser's. A tablet whose timezone was never set is a real device
 * in a real studio, and its idea of 'today' is not the one the schedule runs on
 * (D-68).
 */
export async function fetchDeviceContext(date?: string): Promise<DeviceContext> {
  const query = date ? `?date=${encodeURIComponent(date)}` : ''
  const res = await fetch(`/api/attendance/device${query}`, { headers: deviceHeaders() })

  if (res.status === 401) throw new Error('UNREGISTERED')
  if (!res.ok) throw new Error(`Could not reach the studio (${res.status})`)

  return res.json()
}

/**
 * Where a dancer should be, for the tablet's lookup (D-233).
 *
 * Not `searchDancers` — that one answers with age and household on a substring
 * match, which is a slice of the family directory and not something a screen on
 * a wall gets to hold. This returns a name and a room.
 */
export async function lookupDancer(term: string): Promise<KioskDancerHit[]> {
  const res = await fetch(`/api/attendance/kiosk/dancer-lookup?q=${encodeURIComponent(term)}`)

  // The same 401 the roster distinguishes, and for the same reason (D-209,
  // D-234): a teacher who walked away for fifteen minutes and came back to the
  // open search screen has been locked out, not failed at. Without this she
  // types a name and gets silence.
  if (res.status === 401) throw new Error(SESSION_ENDED)

  if (!res.ok) throw new Error(`Could not look that up (${res.status})`)
  return res.json()
}

export async function fetchMusicFolders(): Promise<MusicFolder[]> {
  const res = await fetch('/api/attendance/device/music', { headers: deviceHeaders() })

  if (!res.ok) throw new Error(`Could not load the music list (${res.status})`)
  return res.json()
}

export async function unlock(staffId: string, pin: string): Promise<{ name: string }> {
  const res = await fetch('/api/attendance/unlock', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...deviceHeaders() },
    body: JSON.stringify({ staffId, pin }),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.title ?? 'That PIN was not recognised.')
  }

  return res.json()
}

export async function fetchSheet(occurrenceId: string): Promise<AttendanceSheet> {
  const res = await fetch(`/api/attendance/sheet/${occurrenceId}`)

  // Two different refusals, and telling them apart is the whole point (D-209).
  if (res.status === 401) throw new Error(SESSION_ENDED)
  if (res.status === 403) throw new Error(NOT_YOURS)

  if (!res.ok) throw new Error(`Could not load the roster (${res.status})`)
  return res.json()
}

/**
 * What a declaration here would do, writing and sending nothing (D-855).
 *
 * <b>The count comes from here and is never added up in the browser.</b> That is
 * the mistake `CloseTheStudioSheet` recorded once already — a confirm promising a
 * number the write then disagreed with.
 */
export async function fetchNotCalledOffer(occurrenceId: string): Promise<SessionCallOffer> {
  const res = await fetch(`/api/attendance/sheet/${occurrenceId}/not-called`)

  // The same two the other two fetchers here tell apart (D-234, D-233).
  if (res.status === 401) throw new Error(SESSION_ENDED)
  if (res.status === 403) throw new Error(NOT_YOURS)

  if (!res.ok) throw new Error(`Could not read the rehearsal call (${res.status})`)
  return res.json()
}

/**
 * Declare a sub-team not called for this session, or take the declaration back
 * (D-851, D-854).
 *
 * The refusals are named by the server and shown as written: *put dancers on the
 * team* and *this is not your class* send a teacher to two different places, which
 * is why there are several rather than one *could not do that* (D-842).
 */
export async function setNotCalled(
  occurrenceId: string,
  teamId: string,
  notCalled: boolean,
): Promise<{
  outcome: string
  dancersCovered: number
  teamName: string | null
  /**
   * How many families the telling was handed off for (D-869) — **not** how many
   * were told.
   *
   * This was `emailed` and `notified`, two counts because one would lie (D-847).
   * Filling either meant waiting for the whole fan-out: ~24 households is
   * eighteen seconds of provider pacing in production and a hundred and
   * forty-four on QA, with the confirm showing *Saving* throughout. The send
   * runs after the response now, so what was *sent* is not knowable here — a
   * bell reports it when it finishes.
   */
  telling: number
}> {
  const res = await fetch(`/api/attendance/sheet/${occurrenceId}/not-called/${teamId}`, {
    method: notCalled ? 'POST' : 'DELETE',
  })

  // The same two `fetchSheet` tells apart, for the same reason: entering a PIN
  // again and finding a colleague's class are opposite remedies (D-234, D-233).
  if (res.status === 401) throw new Error(SESSION_ENDED)
  if (res.status === 403) throw new Error(NOT_YOURS)

  if (!res.ok) {
    const problem = await res.json().catch(() => null)

    // Both halves: the title says what is wrong and the detail says what to do
    // about it, and a teacher reading only the first would have to guess (D-842).
    const said = [problem?.title, problem?.detail].filter(Boolean).join(' ')

    throw new Error(said || `That did not go through (${res.status})`)
  }

  return res.json()
}

/**
 * The kiosk session has ended and the tap did not land (D-234).
 *
 * A sentinel rather than a status code at the call site, so the screen tests
 * one thing and the reason lives next to the request that knows it.
 */
export const SESSION_ENDED = 'SESSION_ENDED'

/**
 * The register is real, the session is alive, and it is not hers to open (D-233).
 *
 * <b>Kept apart from a dead session, because the remedy is opposite.</b> A 401
 * means enter your PIN; a 403 means this is somebody else's register on a day
 * that is not today, and entering a PIN will fail in exactly the same way. Both
 * were reported as "this tablet has locked itself" until a teacher opening a
 * colleague's class was watched doing it — she would have re-entered her PIN,
 * failed again, and concluded the tablet was broken.
 */
export const NOT_YOURS = 'NOT_YOURS'

export async function markAttendance(
  occurrenceId: string,
  /**
   * Either note omitted means *leave that one alone* (D-639, D-656) - which is
   * what a state button sends, and what the domain reads. Sending one and not
   * the other is the ordinary case, not an edge.
   */
  marks: {
    dancerId: string
    state: AttendanceState
    note?: string | null
    familyNote?: string | null
  }[],
  /**
   * @deprecated Ignored by the server since D-482.
   *
   * It derives this from the session's own date against the studio clock and
   * ignores the body — the field that records a habit of late entry must not
   * be the field a client can switch off, and three clients each deciding one
   * rule is how the fourth gets it wrong. Still sent so an older client's body
   * binds rather than 400s; still accepted here so callers compile.
   */
  backdated = false,
): Promise<{ marked: number; ignored: string[] }> {
  const res = await fetch(`/api/attendance/sheet/${occurrenceId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ marks, backdated }),
  })

  /*
   * A dead session is its own failure, and has to be named (D-234).
   *
   * The roster marks optimistically — the row colours under her thumb before
   * the round trip — and rolls back on any error. A 401 rolled back like
   * everything else meant the row flashed and reverted while she was already
   * looking at the next dancer, so she finished the register believing it was
   * saved and it was not. Attendance is the only data here Jackrabbit cannot
   * regenerate, so this must never be one of the silent ones (D-186, D-209).
   */
  if (res.status === 401) throw new Error(SESSION_ENDED)
  if (res.status === 403) throw new Error(NOT_YOURS)

  if (!res.ok) throw new Error(`Could not save that (${res.status})`)
  return res.json()
}

export type RegisterRow = {
  occurrenceId: string
  className: string
  date: string
  startTime: string | null
  roomName: string | null
  expected: number
  marked: number
  isIncomplete: boolean

  /**
   * Whose register it is (D-743). Empty where nobody is named.
   *
   * The id travels with the name because anything grouping by teacher keys on
   * it - the studio has two Ashleys (D-82, H28).
   */
  teachers: { staffId: string; name: string }[]
}

export async function fetchRegisters(incompleteOnly: boolean): Promise<RegisterRow[]> {
  const res = await fetch(`/api/attendance/missing?incompleteOnly=${incompleteOnly}`)

  // Named for what it fetches, not for its subject: `fetchDancerStanding` and
  // `fetchClassStandings` both threw this same sentence, and a screen joining
  // two of them could not say which one failed (D-209).
  if (!res.ok) throw new Error(`Could not load what still needs marking (${res.status})`)
  return res.json()
}

export type MissedSession = {
  occurrenceId: string
  className: string
  date: string
  label: string
}

/**
 * What this dancer may make up **on this night** (D-795).
 *
 * `onOccurrenceId` is what lets the server apply D-274's rule that a makeup is
 * the same kind of dance. Without it the list is every unrepaired absence she
 * has, which is how a jazz register spent months offering a ballet class to
 * make up. Always pass it from a register picker; the only caller that may
 * leave it out is one asking what she owes in general.
 */
export async function fetchUnrepairedAbsences(
  dancerId: string,
  onOccurrenceId?: string,
): Promise<MissedSession[]> {
  const query = onOccurrenceId ? `?on=${encodeURIComponent(onOccurrenceId)}` : ''
  const res = await fetch(`/api/attendance/absences/${dancerId}${query}`)

  if (!res.ok) throw new Error(`Could not load their absences (${res.status})`)
  return res.json()
}

export async function addMakeupVisitor(
  occurrenceId: string,
  dancerId: string,
  missedOccurrenceId: string | null,
): Promise<{ outcome: string; dancerName: string | null }> {
  const res = await fetch(`/api/attendance/sheet/${occurrenceId}/makeup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dancerId, missedOccurrenceId }),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? `Could not add them (${res.status})`)
  }

  return res.json()
}

/**
 * Takes a visitor back off this night (D-806).
 *
 * **The undo for a wrongly-tapped drop-in.** Nothing deleted an attendance
 * record before this, so a mis-added dancer was permanent — while the add
 * endpoint already told teachers to *"Remove her from this session first"*.
 *
 * `freedAbsence` says whether an absence went back to needing a make-up, which
 * is the half of the undo that is invisible on the register.
 */
export async function removeMakeupVisitor(
  occurrenceId: string,
  dancerId: string,
): Promise<{ outcome: string; dancerName: string; freedAbsence: boolean }> {
  const res = await fetch(`/api/attendance/sheet/${occurrenceId}/makeup/${dancerId}`, {
    method: 'DELETE',
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.error ?? `Could not remove them (${res.status})`)
  }

  return res.json()
}

/**
 * Groups registers by date, most recent first.
 *
 * The question behind this screen is "what did we miss?", and the answer is
 * always anchored to a night — "last Thursday" rather than "Ballet 2".
 */
export function byNight(rows: RegisterRow[]): { date: string; rows: RegisterRow[] }[] {
  const nights: { date: string; rows: RegisterRow[] }[] = []

  for (const row of rows) {
    const night = nights.find((n) => n.date === row.date)
    if (night) night.rows.push(row)
    else nights.push({ date: row.date, rows: [row] })
  }

  return nights
}

/** One teacher's registers over the window (D-743). */
export type TeacherRegisters = {
  /** Null for the sessions nobody is named on, which get their own group. */
  staffId: string | null
  teacher: string | null
  taken: number
  outstanding: number
  rows: RegisterRow[]
}

/**
 * The same registers, grouped by whose they are (D-743).
 *
 * `asked`, from QA: *"there's no way for tonya to view like attendance taken
 * broken down by teacher, this would be good so she can see easier who is and
 * who isn't taking attendance"*. So the question is about a **person**, and the
 * answer has to include the ones already done — a list of gaps alone cannot
 * tell somebody who has taken forty registers and missed one from somebody who
 * has taken none.
 *
 * **Worst first**, because that is what she opened the screen for. Ties go
 * alphabetically so the order is stable between visits rather than shuffling
 * on every refetch.
 *
 * **A co-taught night counts for both of them** (D-537): each is expected in
 * the room and either could have taken it, so hiding it from one would let a
 * pair of teachers each believe the other was chased.
 *
 * **Nobody named is its own group, never folded into a teacher.** A private
 * can genuinely have no teacher on it (D-730) and an imported class can have no
 * instructor — attributing those to somebody is how a screen that exists to
 * name a person starts naming the wrong one.
 */
export function byTeacher(rows: RegisterRow[]): TeacherRegisters[] {
  const groups = new Map<string | null, TeacherRegisters>()

  const into = (teacher: { staffId: string; name: string } | null, row: RegisterRow) => {
    // **By id, never by name** (D-82, H28). The studio has two Ashleys, and a
    // by-teacher view keyed on the name merges them into one lane and one
    // accusation - which is why the row carries the id at all.
    const key = teacher?.staffId ?? null

    const group =
      groups.get(key) ??
      { staffId: teacher?.staffId ?? null, teacher: teacher?.name ?? null, taken: 0, outstanding: 0, rows: [] }

    group.rows.push(row)
    if (row.isIncomplete) group.outstanding += 1
    else group.taken += 1

    groups.set(key, group)
  }

  for (const row of rows) {
    /*
     * <b>A register with nobody enrolled is neither taken nor owed</b> (D-743,
     * found by the audit). `IsIncomplete` is `expected > 0 && marked <
     * expected`, so a session nobody was ever in came through as **not
     * incomplete** and landed in `taken` - and on the studio's own data that is
     * about one session in nine, so a teacher whose classes are empty read as
     * *All 33 taken* having marked nothing. There was nothing to take, and a
     * number that counts it is worse than no number on the one screen built to
     * produce it.
     */
    if (row.expected === 0) continue

    if (row.teachers.length === 0) into(null, row)
    else for (const teacher of row.teachers) into({ staffId: teacher.staffId, name: teacher.name }, row)
  }

  /*
   * <b>Worst first, and nobody-named last whatever its count</b> (D-743, the
   * second half found by the audit).
   *
   * Sorted on the count alone it led the list, because classes with no
   * instructor at all outnumber any one teacher's gaps - ten of them in a
   * four-week window on the real studio. That is a gap in the class records
   * rather than somebody to go and ask, and it must not push the people she
   * came to find below the fold.
   */
  return [...groups.values()].sort(
    (a, b) =>
      Number(a.staffId === null) - Number(b.staffId === null) ||
      b.outstanding - a.outstanding ||
      (a.teacher ?? '').localeCompare(b.teacher ?? ''),
  )
}

/**
 * The three buttons a teacher actually taps, in the order she thinks in.
 *
 * `Unmarked` is deliberately absent: it is a starting state, not a choice.
 * Offering it as a button would invite un-marking a dancer, which is a
 * different act from correcting one and is not what anyone means.
 *
 * **`Excused` was removed here** (D-188). Forgiving an absence changes whether
 * a dancer keeps her place on a competitive team, and if a teacher can do it
 * from her phone then it is not Tonya's decision. It remains a *state* — Tonya
 * sets it from her own screen — just not something anybody taps in the room.
 */
export const MARK_OPTIONS: { state: AttendanceState; label: string; short: string }[] = [
  { state: 'Present', label: 'Here', short: 'H' },
  { state: 'Late', label: 'Late', short: 'L' },
  { state: 'Unexcused', label: 'Absent', short: 'A' },
  /*
   * Watching is a fourth button rather than a note on an absence (D-634).
   * The teacher can see it from the door - she is in the room, sitting out -
   * which is what separates it from an excusal she must not be able to grant
   * (D-188). It counts against the dancer exactly as Absent does.
   */
  { state: 'Watched', label: 'Watched', short: 'W' },
]

/**
 * Words a teacher uses, not the enum.
 *
 * "Unexcused" is what the rule is called; "Absent" is what she means when she
 * looks at an empty spot on the floor. `Excused` is no longer a button but is
 * still a state that can arrive from Tonya's screen, so it keeps a label —
 * a deliberately neutral one, because an excusal is not public knowledge and
 * this label renders on a teacher's phone.
 */
export function stateLabel(state: AttendanceState): string {
  if (state === 'Excused') return 'Absent'

  /*
   * ⚠ Named before the fall-through, because the fall-through is wrong for it and
   * nothing would have said so (D-842).
   *
   * This function ends '?? Not marked' and is therefore total by construction:
   * adding a state to the union compiles clean, typecheck says nothing, and the
   * register reads *27 of 27 marked* beside 27 rows labelled **Not marked** - the
   * one state this feature exists to be distinguishable from, and the one that
   * means somebody still owes this register (D-118). A red team found it; the
   * compiler could not.
   *
   * 'Not called' rather than 'Not expected': on a rehearsal the studio's own word
   * is a call, and it says a decision was made rather than that nobody has got to
   * her yet.
   */
  if (state === 'NotExpected') return 'Not called'

  return MARK_OPTIONS.find((o) => o.state === state)?.label ?? 'Not marked'
}

/**
 * How a parent described the absence, in words rather than the enum.
 *
 * **Reads the parent's own list rather than restating it** (D-393). There were
 * two tables for one set of four reasons, and they had already drifted: she
 * picked *"Other"* and the register showed her *"Other reason"* — two copies of
 * one idea, and the second never received the first's fixes.
 *
 * **Her word wins**, because the teacher is reading what the parent said. The
 * same call D-380 made about the studio's own vocabulary: a screen does not get
 * to rename what somebody chose.
 */
export function reasonLabel(reason: AbsenceReason | null): string | null {
  if (reason === null) return null

  return ABSENCE_REASONS.find((r) => r.value === reason)?.label ?? null
}

/**
 * Whether the register should refuse a plain tap on this row (D-188).
 *
 * A dancer whose parent reported her out is shown as such and her buttons are
 * inert, so no accidental tap discards the reason, the reporter and the link to
 * a doctor's note. **Not a hard block** — she can still be marked present
 * behind a confirmation, because a dancer who turns up after all and is
 * recorded absent loses attendance she actually earned, on the number that
 * gates competing.
 */
export function needsOverride(dancer: SheetDancer): boolean {
  return dancer.reportedOut && dancer.state !== 'Present' && dancer.state !== 'Late'
}

/** "Reported out · illness · told us Monday". Null when nobody reported anything. */
export function reportedSummary(dancer: SheetDancer, today: Date = new Date()): string | null {
  if (!dancer.reportedOut) return null

  const parts = ['Reported out']

  const reason = reasonLabel(dancer.reason)
  if (reason) parts.push(reason)

  if (dancer.reportedBy) parts.push(`by ${dancer.reportedBy}`)

  if (dancer.reportedAt) {
    const when = new Date(dancer.reportedAt)
    const days = Math.floor((today.getTime() - when.getTime()) / 86_400_000)

    // Relative, because "told us 3 days ago" is what makes a late report
    // obvious. An absolute timestamp needs arithmetic nobody does mid-class.
    if (days <= 0) parts.push('told us today')
    else if (days === 1) parts.push('told us yesterday')
    else parts.push(`told us ${days} days ago`)
  }

  return parts.join(' · ')
}

/** Colour role for a state — semantic, not decorative. */
export function stateTone(state: AttendanceState): 'good' | 'warn' | 'crit' | 'mute' {
  switch (state) {
    case 'Present':
      return 'good'
    case 'Late':
      return 'warn'
    case 'Unexcused':
      return 'crit'
    // An absence in the arithmetic, but she turned up: amber says
    // "counts against her" without reading as not having come (D-634).
    case 'Watched':
      return 'warn'
    case 'Excused':
      return 'mute'
    /*
     * Muted rather than green, and this is the one tone decision with a parent on
     * the other end (D-842). Not 'crit' - she did nothing wrong. Not 'good'
     * either: a tick reads as *she came*, which is a claim about a session she was
     * never asked to. Same tone as an excusal for the same reason, neither helping
     * nor harming, and the **label** is what tells the two apart.
     *
     * Explicit although 'default' already returns this, because a state arriving
     * here by omission is indistinguishable from one somebody decided about.
     */
    case 'NotExpected':
      return 'mute'
    default:
      return 'mute'
  }
}

/**
 * How far through the roster the teacher is.
 *
/**
 * How much of a register is still to do.
 *
 * <b>Visitors are counted, because she has to mark them</b> (D-294, D-480,
 * D-488). A makeup visitor is a body in the room; a register that excludes
 * her can report itself complete while an unmarked child stands in front of
 * the teacher, which is the failure D-186 is about on the one screen where
 * visitors are created.
 *
 * The original reasoning for excluding her was that a visitor arriving
 * mid-register moves the total underneath somebody working through it. That
 * is real and it is the lesser harm: a total that moves is visible and
 * explicable, and a false <i>"Everyone marked"</i> is neither.
 */
export function progress(
  sheet: AttendanceSheet,
): { marked: number; total: number; done: boolean } {
  const all = [...sheet.enrolled, ...sheet.makeupVisitors]

  const total = all.length
  const marked = all.filter((d) => d.state !== 'Unmarked').length

  return { marked, total, done: total > 0 && marked === total }
}

/** `17:15:00` → `5:15`. Seconds are noise on a wall-mounted screen. */
export function clockTime(time: string | null): string {
  if (!time) return ''

  const [hours, minutes] = time.split(':').map(Number)
  const hour = hours % 12 === 0 ? 12 : hours % 12

  return `${hour}:${String(minutes).padStart(2, '0')}`
}

export function meridiem(time: string | null): string {
  if (!time) return ''
  return Number(time.split(':')[0]) < 12 ? 'am' : 'pm'
}

/**
 * What to call a routine on a screen dancers read.
 *
 * The stage name wins when there is one — nobody is looking for
 * `COMP_OPEN | MO 545 | 13/14 | LINE | COMP` (D-115).
 */
export function routineLabel(folder: MusicFolder): string {
  return folder.routineName?.trim() || folder.className
}

/**
 * Filters the music list by anything a dancer might type.
 *
 * Searches the stage name *and* the class name, because a teacher sending
 * someone to "the Monday 5:45 comp folder" is describing the second one.
 */
export function filterMusic(folders: MusicFolder[], query: string): MusicFolder[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return folders

  return folders.filter((f) =>
    `${f.routineName ?? ''} ${f.className}`.toLowerCase().includes(needle),
  )
}

/**
 * A dancer's standing and the class rows that add up to it (D-169).
 *
 * Fetched separately from the record it decorates, so a slow standing query
 * cannot hold up the page — the name, age and sizes are what most visits are
 * for, and they should not wait on attendance arithmetic.
 */
/**
 * The attendance panel, whole (D-589): the standing with its verdicts and the
 * nights she missed with their repairs. One payload because it is one panel,
 * wherever it is opened - the owner's dancer record and a teacher's view of
 * her draw it from here; the parent's portal carries the same two things.
 */
export type AttendancePanelView = {
  standing: DancerStanding
  /*
   * The portal's shape, not this file's `MissedSession`, which is the kiosk
   * picker's four fields. Same word, two records; the panel needs the one
   * with the repair on it.
   */
  missed: PortalMissedSession[]
}

export async function fetchAttendancePanel(dancerId: string): Promise<AttendancePanelView> {
  const res = await fetch(`/api/attendance/panel/${dancerId}`)
  if (!res.ok) throw new Error(`Could not load attendance (${res.status})`)
  return res.json()
}

/**
 * One dancer on the Standing view (D-165, D-590): under the line, or a class
 * under it. `sendable` is the only thing that decides whether the family
 * email can go; an amber row never is.
 */
export type StandingAlert = {
  dancerId: string
  dancerName: string
  householdId: string
  householdName: string
  percentage: number | null
  attended: number
  counted: number
  notYetMarked: number
  recipients: string[]
  blocked: string | null
  state: OverallState
  classesUnder: string[]
  sendable: boolean
  flag: { byName: string; at: string; note: string | null } | null

  /**
   * The figure **this dancer** is held to (D-652).
   *
   * The report's single `threshold` stopped being true of everybody the day the
   * company team existed: she is under at 90 against a studio line of 80. A row
   * colouring or captioning itself from the report's number would say the same
   * false thing the alert email nearly said.
   */
  threshold?: number

  /**
   * She is on the company team (D-763).
   *
   * Said by the server rather than guessed from the threshold: comparing her
   * figure to the studio's would be wrong the day the two are set the same,
   * and silently, on the screen that decides who competes.
   */
  isCompany?: boolean

  /**
   * The last time the studio told this family she is under (D-766).
   *
   * Absent or null means it never has. `recipients` is how many guardians the
   * email actually reached, so a zero there is *told and it went nowhere* —
   * the one case where sending again is obviously right.
   */
  told?: { byName: string; at: string; percentage: number | null; recipients: number } | null
}

export type StandingAlertReport = {
  alerts: StandingAlert[]
  threshold: number
  sendable: number
  blocked: number
  sent?: boolean
  delivered?: number
  failed?: number

  /**
   * How many rows need attention, for the badge beside the tab (D-765).
   *
   * Its own number since the list stopped being only the dancers in trouble:
   * `alerts.length` was the same thing while every row was an alert, and would
   * have read *113* the day the page listed everybody.
   */
  needsAttention?: number
}

export async function fetchStandingAlerts(): Promise<StandingAlertReport> {
  const res = await fetch('/api/attendance/standing-alerts/')
  if (!res.ok) throw new Error(`Could not load standing (${res.status})`)
  return res.json()
}

/** Tell every family that can be told (D-165). Explicit opt-in, like the import. */
export async function sendStandingAlerts(): Promise<StandingAlertReport> {
  const res = await fetch('/api/attendance/standing-alerts/?send=true', { method: 'POST' })
  if (!res.ok) throw new Error(`Could not send (${res.status})`)
  return res.json()
}

/**
 * Tell one family (D-766).
 *
 * The same send as the bulk one, aimed at a row somebody picked by name. The
 * server recomputes her standing and refuses a row that is not sendable, so a
 * dancer who has climbed since the page loaded cannot be emailed about a line
 * she is no longer under.
 */
export async function sendStandingAlert(dancerId: string): Promise<StandingAlertReport> {
  const res = await fetch(`/api/attendance/standing-alerts/dancer/${dancerId}`, { method: 'POST' })
  if (!res.ok) throw new Error(`Could not send (${res.status})`)
  return res.json()
}

/** A teacher's flag on one dancer (D-590): a note to the studio, a mark on the row. */
export async function flagStanding(dancerId: string, note: string | null): Promise<{ byName: string; at: string; note: string | null }> {
  const res = await fetch(`/api/attendance/standing-alerts/${dancerId}/flag`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note }),
  })
  if (!res.ok) throw new Error(`Could not flag (${res.status})`)
  return res.json()
}

/** Every dancer on one class's roster, with both figures (D-169). */
export async function fetchClassStandings(studioClassId: string): Promise<RosterStanding[]> {
  const res = await fetch(`/api/attendance/class-standing/${studioClassId}`)
  if (!res.ok) throw new Error(`Could not load attendance (${res.status})`)
  return res.json()
}

export type RosterStanding = {
  dancerId: string
  name: string
  inThisClass: ClassAttendance
  overall: AttendanceStanding
}

export type StandingTone = 'good' | 'warn' | 'crit' | 'mute'

/**
 * The colour a standing percentage should read as (D-168).
 *
 * **Bands are relative to the configured threshold, never hardcoded.** The 80%
 * rule is a `Setting` Tonya can change, and a green badge at 82% would quietly
 * become a lie the day she raises it to 85.
 *
 * - below the threshold — critical. She is not in good standing.
 * - within ten points above it — warning. One more absence and she is not.
 * - anything higher — good.
 */
export function standingTone(percentage: number | null, threshold: number): StandingTone {
  if (percentage === null) return 'mute'
  if (percentage < threshold) return 'crit'
  if (percentage < threshold + 10) return 'warn'

  return 'good'
}

/**
 * Whether the number is still moving.
 *
 * A percentage over registers nobody has filled in is confidently wrong
 * (D-118), so anywhere it is shown it has to look unfinished — an outline
 * rather than a solid badge — instead of asserting a figure that will change.
 */
export function isProvisional(notYetMarked: number): boolean {
  return notYetMarked > 0
}

/**
 * Tailwind classes for a standing badge.
 *
 * Here rather than in each screen, because Tonya, the teacher and the parent
 * must all read the same colour as the same thing. Three screens each choosing
 * their own thresholds is how "she looked fine on my page" starts.
 */
export function standingClasses(tone: StandingTone, provisional: boolean): string {
  const solid: Record<StandingTone, string> = {
    good: 'bg-good-bg text-good',
    warn: 'bg-warn-bg text-ink-2',
    crit: 'bg-crit-bg text-crit',
    mute: 'bg-surface-2 text-ink-3',
  }

  const outline: Record<StandingTone, string> = {
    good: 'border border-dashed border-good text-good',
    warn: 'border border-dashed border-warn text-ink-2',
    crit: 'border border-dashed border-crit text-crit',
    mute: 'border border-dashed border-line-2 text-ink-3',
  }

  return provisional ? outline[tone] : solid[tone]
}

export type SessionMarker = 'now' | 'next' | null

/**
 * Which card is the class in front of the teacher (D-235).
 *
 * <b>Reuses `standing()` rather than restating the rule.</b> The half-open
 * comparison, the D-84 no-start-time guard and the not-today short-circuit all
 * live there and are already tested; a second copy here is how two screens come
 * to disagree about which class is on — the same reasoning D-199 settled for
 * framing.
 *
 * <b>Exactly one "now" and at most one "next".</b> "Next" is the earliest
 * session still to start, so an evening with three classes ahead of it marks one
 * of them, not three. Both are absent once the last class has ended, which is a
 * real answer rather than a gap.
 */
export function sessionMarkers(
  sessions: SessionOption[],
  nowMinutes: number,
  isToday: boolean,
): Map<string, SessionMarker> {
  const markers = new Map<string, SessionMarker>()
  if (!isToday) return markers

  const upcoming = sessions
    .filter((s) => standing(s, nowMinutes, true) === 'next')
    .sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? ''))

  const next = upcoming[0]

  for (const session of sessions) {
    const where = standing(session, nowMinutes, true)

    if (where === 'now') markers.set(session.occurrenceId, 'now')
    else if (next && session.occurrenceId === next.occurrenceId) markers.set(session.occurrenceId, 'next')
  }

  return markers
}

/**
 * How the day's heading reads.
 *
 * Names the date rather than saying "today", because after D-235 the screen is
 * often not showing today — and "Nothing scheduled in the studio today" on a
 * Saturday the teacher deliberately navigated to is a screen contradicting the
 * person using it (D-189's family of silent wrongness).
 */
export function dayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })
}
